export type RetryOptions = {
  retries?: number; // total attempts including the first (default 4)
  baseMs?: number;  // base delay (default 100ms)
  factor?: number;  // exponential factor (default 2)
  maxDelayMs?: number; // cap the delay (default 1500ms)
  jitter?: boolean; // add jitter 0.5x..1.5x (default true)
};

function sleep(ms: number) {
  return new Promise((r) => setTimeout(r, ms));
}

function computeDelay(attempt: number, {
  baseMs = 100,
  factor = 2,
  maxDelayMs = 1500,
  jitter = true,
}: RetryOptions = {}) {
  let delay = Math.min(baseMs * Math.pow(factor, attempt), maxDelayMs);
  if (jitter) {
    const j = 0.5 + Math.random(); // 0.5 .. 1.5
    delay = Math.round(delay * j);
  }
  return delay;
}

/**
 * ¿El error es una caída de infraestructura? Sólo estos valen para reintentar y,
 * si persisten, para abrir el circuit breaker.
 *
 * OJO con lo que NO entra: los códigos `PGRST*` son errores de **aplicación** de
 * PostgREST, no caídas. `PGRST116` es la respuesta SANA de un `.single()` que
 * trae 0 filas, y la versión anterior la clasificaba como 503 (`code.startsWith('PGRST')`):
 * un resultado vacío perfectamente normal abría el breaker 25s y, a los 3, tapaba
 * la app con la pantalla de "Servidores en mantenimiento".
 * Ver ADR-2026-08-02-no-company-associated-diagnostico.
 *
 * Los `PGRST*` que sí son infra (PGRST001 sin conexión a la DB, PGRST002 con el
 * schema cache caído) llegan igual con status 5xx o con "schema cache" en el
 * mensaje, así que siguen cubiertos por las reglas de abajo.
 */
function isInfraFailure(error: any): boolean {
  if (!error) return false;
  // supabase-js PostgREST error typically has status
  const status = (error as any).status;
  if (typeof status === 'number' && status >= 500) return true;
  const msg = String((error as any).message || '').toLowerCase();
  if (msg.includes('service unavailable') || msg.includes('503') || msg.includes('schema cache') || msg.includes('retry')) return true;
  return false;
}

type CircuitState = { open: boolean; hard: boolean; until: number };
const BREAKER_COOLDOWN_MS = 25000;
// Cuántas aperturas "frescas" seguidas del breaker (cada una ≈ un ciclo de ~25s
// con Supabase caído) escalan de BANNER a PANTALLA COMPLETA. Con 3, un blip
// transitorio queda en banner y una caída sostenida (~1 min) tapa la app entera.
// Ver ADR-136.
const HARD_OPEN_TRIPS = 3;
let breakerUntil = 0;
let consecutiveTrips = 0;
let hardOpen = false;
const circuitListeners = new Set<(s: CircuitState) => void>();

function nowMs() { return Date.now(); }

function emitCircuit() {
  const s = getCircuitState();
  circuitListeners.forEach((fn) => { try { fn(s); } catch {} });
}

export function isCircuitOpen(): boolean {
  return nowMs() < breakerUntil;
}

// Hard-open = varios ciclos de fallo seguidos → la app se considera rota y se
// muestra la PANTALLA COMPLETA (no solo el banner). Sticky hasta `resetCircuit`.
export function isCircuitHardOpen(): boolean {
  return hardOpen;
}

export function getCircuitState(): CircuitState {
  return { open: isCircuitOpen(), hard: hardOpen, until: breakerUntil };
}

export function onCircuitChange(fn: (s: CircuitState) => void) {
  circuitListeners.add(fn);
  return () => { circuitListeners.delete(fn); };
}

export function tripCircuit(extraMs: number = BREAKER_COOLDOWN_MS) {
  const target = nowMs() + extraMs;
  const wasOpen = isCircuitOpen();
  if (target > breakerUntil) breakerUntil = target;
  // Cada apertura "fresca" (el breaker venía cerrado) = un ciclo de fallo. Varios
  // seguidos sin recuperarse → escalamos a hard-open (pantalla completa).
  if (!wasOpen) {
    consecutiveTrips += 1;
    if (!hardOpen && consecutiveTrips >= HARD_OPEN_TRIPS) hardOpen = true;
    emitCircuit();
  }
  setTimeout(() => {
    if (!isCircuitOpen()) emitCircuit();
  }, extraMs + 25);
}

// Recuperación: cierra el breaker, limpia el contador y baja el hard-open. La
// llama una lectura real que sale bien (`retrySupabase`) o el probe de salud que
// corre mientras la pantalla completa está puesta. Ver ADR-136.
export function resetCircuit() {
  const changed = isCircuitOpen() || hardOpen || consecutiveTrips > 0;
  breakerUntil = 0;
  consecutiveTrips = 0;
  hardOpen = false;
  if (changed) emitCircuit();
}

/**
 * Reintenta una llamada de Supabase (lectura/idempotente) cuando falla por
 * infraestructura. Espera que `run()` devuelva el shape estándar de supabase
 * (`{ data, error, ... }`) y devuelve ese mismo shape.
 *
 * Antes esta función **no reintentaba nunca**: todas las ramas del `for`
 * retornaban dentro de `attempt === 0`, así que `sleep()` y `computeDelay()`
 * eran código muerto. Un blip de red dejaba la lectura fallada hasta que el
 * usuario recargaba a mano. Ver ADR-2026-08-02-no-company-associated-diagnostico.
 */
export async function retrySupabase<T = any>(
  run: () => PromiseLike<T> | T,
  options: RetryOptions = {}
): Promise<T> {
  const { retries = 4 } = options;
  if (isCircuitOpen()) {
    return { data: null, error: { status: 503, message: 'Service Unavailable (circuit open)' } } as unknown as T;
  }
  let last: any = { data: null, error: null };
  for (let attempt = 0; attempt < retries; attempt++) {
    try {
      const res: any = await run();
      if (!res?.error) {
        // Lectura OK → Supabase respondió; si venía fallando, es recuperación.
        if (consecutiveTrips > 0 || hardOpen) resetCircuit();
        return res;
      }
      last = res;
      // Error de aplicación (0 filas, constraint, permisos…): es la respuesta
      // final del server. Reintentar no la cambia, así que se devuelve ya.
      if (!isInfraFailure(res.error)) return res;
    } catch (e: any) {
      // Un throw es transporte (fetch cortado, DNS, timeout): siempre vale
      // reintentarlo. Igual no cuenta como "Supabase caído" — puede ser la
      // conexión del usuario, que tiene su propio cartel de offline.
      last = { data: null, error: e };
    }
    // Backoff entre intentos; después del último no hay nada que esperar.
    if (attempt < retries - 1) await sleep(computeDelay(attempt, options));
  }
  // Recién con todos los intentos agotados esto es una caída: un 503 suelto que
  // se recupera en el reintento ya no abre el breaker.
  if (isInfraFailure(last?.error)) tripCircuit();
  return last as T;
}
